import React from 'react';

import styled, { useTheme } from 'styled-components';

import { AnimatePresence } from 'framer-motion';

import { Translate } from 'react-localize-redux';

import SimpleArtist from '@common/types/artist/simple-artist';

import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Pill from '@components/basics/pill';
import DotsIcon from '@components/basics/icons/dots';
import SquareImg from '@components/basics/artwork-img';
import Typography from '@components/basics/typography';
import FixedBottomMenu from '@components/layout/fixed-bottom-menu';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';
import MerchColorChoice from '@components/merch-row/merch-color-choice';
import { NFTType } from '@webapp/pages/catalog/nft-tab';
import StatusLabel from '@components/status-label';
import { getNFTMenuOptions } from '@components/nft-row/menu-options';
import { formatNumber } from '@webapp/pages/earnings';
import moment from 'moment';

declare type MobileInfoRowProps = {
  className?: string;
  item: NFTType;
  artist: SimpleArtist;
  isListOpen: boolean;
  isArtist?: boolean;
  openList: () => void;
  handleCatalogMenu: (action: string, release: any) => void;
};

export default function MobileInfoRow({
  className,
  item,
  artist,
  isListOpen,
  openList,
  isArtist,
  handleCatalogMenu,
}: MobileInfoRowProps) {
  const theme = useTheme();
  const menuOptions = getNFTMenuOptions(item, artist);

  const [dropdown, setDropdown] = React.useState<boolean>(false);

  return (
    <Row className={`${className || ''} ${isListOpen ? 'menu-opened' : ''}`}>
      <Flex justifyContent="space-between" alignItems="center" gap={20}>
        <Flex alignItems="center" gap={20}>
          <SquareImg size={36} srcImg={item.image} altImg={item.title} />
          <Grid gap={5}>
            <Typography className="merch-name" customFont="semi-bold">
              {item.title}
            </Typography>
            {item.upc && (
              <Typography type="small" color={theme.colors.tertiary}>
                {item.upc}
              </Typography>
            )}
            {item.colors && <MerchColorChoice colors={item.colors} />}
          </Grid>
        </Flex>
        <Pill>{item.type}</Pill>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" gap={20}>
        <Typography weight="700">
          <Translate id="CATALOG.HEADER.DATE_ADDED" />
        </Typography>
        <Typography color={theme.colors.tertiary}>
          {moment(item.date_added).format('DD MMM YY')}
        </Typography>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" gap={20}>
        <Typography weight="700">
          <Translate id="CATALOG.HEADER.EDITIONS" />
        </Typography>
        <Typography color={theme.colors.tertiary}>
          {formatNumber(item.editions)}
        </Typography>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" gap={10}>
        <StatusLabel
          status={item.status}
          approved={item.status ? true : false}
        />
        <Flex justifyContent="flex-end" alignItems="center" gap={10}>
          {!isArtist ? (
            <StyledCircleArrowIcon
              isWhite
              smallArrow
              arrowUp={isListOpen}
              onClick={openList}
            />
          ) : null}
          {menuOptions.length ? (
            <>
              <StyledDotsIcon onClick={() => setDropdown(true)} />
              <AnimatePresence exitBeforeEnter>
                {dropdown && (
                  <FixedBottomMenu
                    title={item.title}
                    options={menuOptions}
                    isShowing={dropdown}
                    onSelect={(action: string) =>
                      handleCatalogMenu(action, item)
                    }
                    onClose={() => setDropdown(false)}
                  />
                )}
              </AnimatePresence>
            </>
          ) : null}
        </Flex>
      </Flex>
    </Row>
  );
}

const Row = styled.div`
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  width: 100%;
  display: grid;
  grid-gap: 10px;
  padding: 20px;

  &.menu-opened {
    border-radius: 10px 10px 0 0;
  }
`;

const StyledDotsIcon = styled(DotsIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const StyledCircleArrowIcon = styled(CircleArrowIcon)`
  width: 32px;
  height: 32px;
`;
